import { Controller } from '@hotwired/stimulus'
import type { AlertType } from './types'
import './styles.less'

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = ['container', 'message']

    declare readonly hasContainerTarget: boolean
    declare readonly containerTarget: HTMLDivElement
    declare readonly hasMessageTarget: boolean
    declare readonly messageTargets: HTMLDivElement[]

    private nodeTimeouts: Map<HTMLDivElement, number> = new Map()

    connect() {
        if (!this.hasContainerTarget) {
            return
        }

        this.displayContainsMessages()

        window.addEventListener('alert:show', this.handleShow as EventListener)
    }

    disconnect() {
        window.removeEventListener('alert:show', this.handleShow as EventListener)
    }

    show(message: string, type: AlertType, timeout?: number) {
        if (!message || !this.hasContainerTarget) {
            return
        }

        this.hideAll()

        const node = this.createMessageNode(message, type)
        this.containerTarget!.append(node)

        requestAnimationFrame(() => node.classList.add('show'))

        this.setupAutoClose(node, timeout)

        return node
    }

    hide(node: HTMLDivElement) {
        if (!node) {
            return
        }

        const id = this.nodeTimeouts.get(node)

        if (typeof id === 'number') {
            clearTimeout(id)
            this.nodeTimeouts.delete(node)
        }

        node.addEventListener('transitionend', () => node.remove(), { once: true })
        node.classList.remove('show')
    }

    hideAll() {
        if (!this.hasContainerTarget || !this.hasMessageTarget) {
            return
        }

        this.messageTargets.forEach((node) => this.hide(node as HTMLDivElement))
    }

    private handleShow = (e: CustomEvent<{ message: string; type?: AlertType; timeout?: number }>) => {
        const { message, type = 'default', timeout } = e.detail || ({} as any)
        this.show(message, type, timeout)
    }

    private createMessageNode(message: string, type: AlertType) {
        const wrapper = document.createElement('div')
        wrapper.className = `alert__message ${type}`

        const body = document.createElement('div')
        body.className = 'alert__message--body'

        const btn = document.createElement('button')

        btn.className = 'button alert__message--close'
        btn.setAttribute('data-action', 'click->alert#handleClose')
        btn.innerHTML = `
            <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.41 4.41a.833.833 0 011.18 0L10 8.822l4.41-4.41a.833.833 0 111.18 1.178L11.177 10l4.411 4.41a.833.833 0 01-1.178 1.18L10 11.177 5.59 15.59a.833.833 0 11-1.18-1.178L8.822 10l-4.41-4.41a.833.833 0 010-1.18z" fill="var(--bg-brand-primary-04)"></path>
            </svg>
        `

        const content = document.createElement('div')
        content.className = 'alert__message--content t-body'
        content.innerHTML = message

        body.append(btn)
        body.append(content)
        wrapper.append(body)
        wrapper.setAttribute('data-alert-target', 'message')

        return wrapper
    }

    private handleClose(event: Event) {
        const target = event.currentTarget as HTMLElement
        const node = target?.closest('.alert__message') as HTMLDivElement | null

        if (!node) {
            return
        }

        this.hide(node)
    }

    private setupAutoClose(node: HTMLDivElement, timeout?: number) {
        const explicit = typeof timeout === 'number' && timeout > 0 ? timeout : null
        const fromNode = parseInt((node.getAttribute('data-timeout') || '') as string)
        const nodeTimeout = Number.isFinite(fromNode) && fromNode > 0 ? fromNode : null

        const ms = explicit ?? nodeTimeout ?? null

        if (!ms || ms <= 0) {
            return
        }

        const id = window.setTimeout(() => this.hide(node), ms)

        this.nodeTimeouts.set(node, id)
    }

    private displayContainsMessages() {
        if (!this.hasContainerTarget || !this.hasMessageTarget) {
            return
        }

        this.messageTargets.forEach((node) => {
            node.classList.add('show')
            this.setupAutoClose(node)
        })
    }
}
